import { History, Star } from 'lucide-react';
import { PlanVipTagBadge } from '@/components/plans/PlanVipTagBadge';
import { getPlanTheme } from '@/components/plans/planTheme';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

export interface VipAssinaturaPlanosBlockProps {
    user: any;
    vipPlans: any[];
    /** True enquanto se gera o link de pagamento (desativa «Assinar»). */
    plansLoading: boolean;
    onSelectPlan: (plan: any) => void;
    showPaymentsHistory: () => void;
    /** `embedded`: só a grelha de planos (ex.: perfil, com cabeçalho à parte). */
    variant?: 'full' | 'embedded';
}

export function VipAssinaturaPlanosBlock({
    user,
    vipPlans,
    plansLoading,
    onSelectPlan,
    showPaymentsHistory,
    variant = 'full',
}: VipAssinaturaPlanosBlockProps) {
    const embedded = variant === 'embedded';

    const plansGrid = (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {vipPlans.map((plan: any) => {
                const theme = getPlanTheme(plan.name ?? '');
                return (
                    <div
                        key={plan.id}
                        className={`relative flex flex-col rounded-xl border p-4 transition-all overflow-hidden ${theme.border} ${theme.bg} ${theme.hoverBorder} ${theme.hoverBg}`}
                    >
                        {/* Linha decorativa no topo */}
                        <div className={`absolute top-0 left-0 right-0 h-[2px] ${theme.topLine}`} />

                        {String(plan.name ?? '').toLowerCase().includes('experiente') && (
                            <div className="absolute right-0 top-0 flex items-center gap-0.5 rounded-bl-lg rounded-tr-xl bg-orange-500 px-1.5 py-0.5 text-[9px] font-bold text-white">
                                <Star size={8} fill="currentColor" /> TOP
                            </div>
                        )}

                        <h3 className={`mb-1 pr-8 text-sm font-bold leading-tight ${theme.nameColor}`}>{plan.name}</h3>
                        <PlanVipTagBadge plan={plan} size="sm" className="mb-1.5" />
                        <div className="mb-1 text-base font-bold text-white">
                            R$ {Number(plan.pricing ?? 0).toFixed(2).replace('.', ',')}
                        </div>
                        <div className="mb-3 text-xs text-white/40">{plan.validityDays ?? 0} dias</div>

                        <p className="mb-4 flex-1 text-xs leading-relaxed text-white/50">{plan.description ?? ''}</p>

                        <button
                            type="button"
                            onClick={() => onSelectPlan(plan)}
                            className={`w-full rounded-lg py-2 text-xs font-bold transition-all disabled:opacity-50 ${theme.buttonBase} ${theme.buttonHover}`}
                            disabled={plansLoading}
                        >
                            {plansLoading ? '…' : 'Assinar'}
                        </button>
                    </div>
                );
            })}
        </div>
    );

    if (embedded) {
        return <div className="mt-4">{plansGrid}</div>;
    }

    return (
        <section className="mb-2">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-bold text-white md:text-2xl">Planos VIP</h1>
                {user?.is_vip && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-full shadow-lg shadow-green-500/10">
                        <div className="h-2 w-2 animate-pulse rounded-full bg-green-400" />
                    </div>
                )}
            </div>
            <p className="text-sm text-white/50 mb-6 max-w-2xl">
                Renova ou estende o teu VIP. Ao assinar, serás redirecionado para o pagamento seguro.
            </p>

            {user?.is_vip && user.vip_acaba_em && (
                <div className="mb-6 rounded-lg border border-orange-500/20 bg-gradient-to-br from-orange-500/10 to-neutral-900/50 p-5">
                    <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                        <div>
                            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-orange-400">Assinatura ativa</div>
                            <h2 className="mb-1 text-xl font-bold text-white">Membro VIP</h2>
                            <p className="text-sm text-white/50">Expira em {dayjs(user.vip_acaba_em).format('DD [de] MMMM, YYYY')}</p>
                        </div>
                        <button
                            type="button"
                            onClick={showPaymentsHistory}
                            className="flex shrink-0 items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/10"
                        >
                            <History size={16} /> Histórico de pagamentos
                        </button>
                    </div>
                </div>
            )}

            {!user?.is_vip ? (
                <div className="mb-6 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/60">
                    Ainda não tens VIP ativo. Escolhe um plano abaixo para começar.
                </div>
            ) : null}

            {plansGrid}
        </section>
    );
}
